import posthog from "posthog-js";
import Clarity from "@microsoft/clarity";

// ---------- User Identity ----------

type UserInfo = {
  id: number;
  first_name: string;
  last_name?: string;
  email?: string;
  mobile?: string;
  user_type?: string;
};

export function identifyUser(user: UserInfo) {
  posthog.identify(String(user.id), {
    name: `${user.first_name} ${user.last_name || ""}`.trim(),
    email: user.email,
    phone: user.mobile,
    user_type: user.user_type,
  });

  try {
    Clarity.identify(String(user.id), undefined, undefined, user.first_name);
  } catch {}

  if (user.user_type && typeof window !== "undefined" && typeof (window as any).clarity === "function") {
    (window as any).clarity("set", "user_type", user.user_type);
  }
}

export function resetUser() {
  posthog.reset();
}

// ---------- Clarity Events ----------

function clarityEvent(name: string) {
  if (typeof window !== "undefined" && typeof (window as any).clarity === "function") {
    (window as any).clarity("event", name);
  }
}

function clarityTag(key: string, value: string) {
  if (typeof window !== "undefined" && typeof (window as any).clarity === "function") {
    (window as any).clarity("set", key, value);
  }
}

export function setClarityTag(key: string, value: string) {
  clarityTag(key, value);
}

// ---------- Custom Events ----------

export function trackPrebookSubmitted(props: { user_type: string; quantity: number }) {
  posthog.capture("prebook_submitted", props);
  clarityEvent(`prebook_submitted_${props.user_type}`);
}

export function trackPaymentSuccess(props: { flow: string; user_type: string; payment_id: string; order_id: string }) {
  posthog.capture("payment_success", props);
  clarityEvent(`payment_success_${props.user_type}`);
  clarityTag("payment_status", "success");
}

export function trackPaymentFailed(props: { flow: string; user_type: string; order_id: string }) {
  posthog.capture("payment_failed", props);
  clarityTag("payment_status", "failed");
}

export function trackPaymentInitiated(props: { flow: string; amount: number; user_type?: string }) {
  posthog.capture("payment_initiated", props);
  clarityEvent(props.user_type ? `payment_initiated_${props.user_type}` : "payment_initiated");
  clarityTag("payment_status", "initiated");
}

export function trackCtaClicked(props: { cta: string; page: string }) {
  posthog.capture("cta_clicked", props);
  clarityEvent(`cta_${props.cta}`);
  clarityTag("page_type", props.page);
}

export function trackExitIntent() {
  posthog.capture("exit_intent");
}

// ---------- Funnel Events ----------

export function trackOtpRequested(props: { user_type: string; page: string }) {
  posthog.capture("otp_requested", props);
  clarityEvent(`otp_requested_${props.user_type}`);
}

export function trackOtpVerified(props: { user_type: string; page: string }) {
  posthog.capture("otp_verified", props);
  clarityEvent(`otp_verified_${props.user_type}`);
}

export function trackPlanSelected(props: { plan: string; page: string }) {
  posthog.capture("plan_selected", props);
  clarityEvent("plan_selected");
}

// ---------- Registration Funnel Events ----------

export function trackRegistrationFormSubmitted(props: { user_type: string; page: string }) {
  posthog.capture("registration_form_submitted", props);
}

export function trackContractSigned(props: { page: string }) {
  posthog.capture("contract_signed", props);
}

export function trackRegistrationPaymentInitiated(props: { user_type: string; page: string }) {
  posthog.capture("registration_payment_initiated", props);
}
